import Konva from 'konva/src/Core';
import 'konva/src/Shapes/Image';
import fontIcons from 'common/fontIcons';
import _ from 'common/customLodash';
import helper from 'common/helper';
import DesignControl from './designControl';
import DesignDescription from './designDescription';
import PlacingComponent from 'placingEditor/placingComponent';

class Design extends PlacingComponent {
    constructor(design, imageObj, editable = true) {
        super();

        this.isSelected = false;
        this.data = design;
        this.imageObj = imageObj;
        this.componentSize = 20;
        this.controls = [];
        this.labels = [];
        this.resizable = !!design.resizable;
        this.content = new PlacingComponent();
        this.addKonvaComponent(this.content);
        this.setImage(imageObj, design.width, design.height);

        if (editable) {
            this.addControls();
        } else if (design.id !== undefined) {
            this.addLabel(design.id.toString(), 4);
        }
    }

    setImage(imageObj, width, height) {
        this.design = new Konva.Image({
            image: imageObj,
            x: this.componentSize,
            y: this.componentSize
        });
        this.content.addKonvaNode(this.design);

        if (_.isUndefined(width) && !_.isUndefined(height)) {
            width = height * (imageObj.naturalWidth / imageObj.naturalHeight);
        } else if (!_.isUndefined(width) && _.isUndefined(height)) {
            height = width * (imageObj.naturalHeight / imageObj.naturalWidth);
        } else if (_.isUndefined(width) && _.isUndefined(height)) {
            var currentWidth = this.getDesignSize().width;
            var currentHeight = this.getDesignSize().height;
            var factor = currentHeight / 50;
            height = 50;
            width = currentWidth / factor;
        }

        this.setDesignSize({width, height});
    }

    addControls() {
        const rotateCtrl = new DesignControl(this.componentSize, fontIcons.rotate, 1);
        this._resizeCtrl = new DesignControl(this.componentSize, fontIcons.expand, 2);
        const deleteCtrl = new DesignControl(this.componentSize, fontIcons.trash, 3);
        const instructionCtrl = new DesignControl(this.componentSize, fontIcons.instruction, 4);
        this.addControlEvent(rotateCtrl, 'rotate');
        this.addControlEvent(deleteCtrl, 'delete');
        this.addControlEvent(instructionCtrl, 'instruction');
        this.addComponent(rotateCtrl, 'control');
        this.addComponent(deleteCtrl, 'control');
        this.addComponent(instructionCtrl, 'control');
        this.addControlEvent(this._resizeCtrl, 'resize');
        this.addComponent(this._resizeCtrl, 'control');
    }

    addLabel(text, placement) {
        const label = new DesignDescription(placement, '#dc574e', '#fff');
        label.setText(text);
        this.addComponent(label, 'label');
    }

    addControlEvent(control, eventName) {
        control.events.on('mousedown', evt => {
            evt.cancelBubble = true;
            this.events.emit(eventName, {x: evt.evt.layerX, y: evt.evt.layerY});
        });
    }

    addComponent(component, type) {
        this[type + 's'].push(component);
        this.content.addKonvaComponent(component);
        this.placeComponent(component);
        const isVisible = type === 'label';
        component.setVisible(isVisible);
    }

    placeComponent(component) {
        const designSize = this.getDesignSize();
        const position = {x: 0, y: 0};
        const right = designSize.width + this.componentSize;
        const bottom = designSize.height + this.componentSize;
        const centerX = designSize.width / 2;
        const centerY = designSize.height / 2;

        switch (component.placement) {
            case 2:
                position.x = right;
                break;
            case 3:
                position.y = bottom;
                break;
            case 4:
                position.x = right;
                position.y = bottom;
                break;
            case 5:
                position.x = centerX;
                position.y = centerY;
        }
        component.setPos(position);
    }

    setStroke() {
        if (this.isSelected) {
            this.design.stroke('#eaeaea');
        } else {
            this.design.stroke(null);
        }
    }

    getDesignOffset() {
        return this.design.getPosition();
    }

    getDesignLayerPos() {
        const localDesignPos = this.design.getPosition();
        const position = this.getPos();

        return {
            x: position.x + localDesignPos.x,
            y: position.y + localDesignPos.y
        };
    }

    setDesignLayerPos({x, y}) {
        if (!_.isUndefined(x)) {
            this.setPos({x: (x - this.design.getPosition().x)});
        }
        if (!_.isUndefined(y)) {
            this.setPos({y: (y - this.design.getPosition().y)});
        }
    }

    getDesignCenterLayerPos() {
        const pos = this.getDesignLayerPos();
        const size = this.getDesignSize();
        return {
            x: pos.x + (size.width / 2),
            y: pos.y + (size.height / 2)
        };
    }

    getDesignSize() {
        return {
            width: this.design.getWidth(),
            height: this.design.getHeight()
        };
    }

    setDesignSize({width, height}) {
        if (!_.isUndefined(width)) {
            this.design.setWidth(width);
        }
        if (!_.isUndefined(height)) {
            this.design.setHeight(height);
        }

        // Use new size for content and components.
        const contentOffset = {
            x: this.design.x() + (this.getDesignSize().width / 2),
            y: this.design.y() + (this.getDesignSize().height / 2)
        };
        this.content.setOffset(contentOffset);
        this.content.setPos({x: contentOffset.x, y: contentOffset.y});
        this.controls.forEach(c => this.placeComponent(c));
        this.labels.forEach(l => this.placeComponent(l));
    }

    setDesignRotation(rotation) {
        this.content.setRotation(rotation);
    }

    getDesignRotation() {
        return this.content.getRotation();
    }

    getDesignCorners() {
        return helper.getCorners(this.design);
    }

    setInstruction(instruction) {
        this.data.instruction = instruction;
    }

    getInstruction() {
        return this.data.instruction || '';
    }

    setReference(reference) {
        this.data.reference = reference;
    }

    getReference() {
        return this.data.reference || '';
    }

    select() {
        this.isSelected = true;
        this.controls.forEach(c => c.setVisible(true));
        if (!this.resizable) {
            this._resizeCtrl.setVisible(false);
        }
        this.setStroke();
    }

    deselect() {
        this.isSelected = false;
        this.controls.forEach(c => c.setVisible(false));
        this.setStroke();
        this.drawParentLayer();
    }

    onMouseDown(evt) {
        evt.cancelBubble = true;
        this.select();
        this.events.emit('select', {x: evt.evt.layerX, y: evt.evt.layerY});
    }
}

export default Design;



// WEBPACK FOOTER //
// ./src/placingEditor/design/design.js